import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Expand } from "lucide-react";
import { PhotoViewer } from "./PhotoViewer";
import { SocialLinks } from "./SocialLinks";
import { company, home, services, projects, about, contact, navLinks } from "@/content/siteContent";

const SectionTitle = ({ title, intro }) => (
  <div className="section-title">
    <h2>{title}</h2>
    {intro && <p>{intro}</p>}
  </div>
);

const Fact = ({ label, value, testId }) => (
  <div className="home-fact" data-testid={testId}>
    <span className="fact-label">{label}</span>
    <span className="fact-value">{value}</span>
  </div>
);

export const HomeSection = () => (
  <section id="home" className="section section-home" data-testid="home-section">
    <div className="container">
     <div className="home-grid">
      <div>
      <p className="eyebrow" data-testid="home-location">{company.location}</p>
      <h1 className="home-heading" data-testid="home-heading">
        {home.heading}
        {home.headingSub && <span>{home.headingSub}</span>}
      </h1>
      <p className="lead" data-testid="home-intro">{home.intro}</p>
      <div className="btn-row">
        <Link to="/contact" className="btn" data-testid="home-contact-button">Contact Us</Link>
        <Link to="/projects" className="btn btn-outline" data-testid="home-projects-button">View Our Work</Link>
      </div>
      </div>
      <figure className="home-photo" data-testid="home-photo">
        <img src={home.photo} alt={home.photoAlt} />
        <figcaption data-testid="home-photo-caption">
          <span className="photo-name">{home.photoName}</span>
          <span className="photo-title">{home.photoTitle}</span>
        </figcaption>
      </figure>
     </div>
      <div className="home-facts">
        <Fact label="Phone" value={company.phone} testId="home-fact-phone" />
        <Fact label="Email" value={company.email} testId="home-fact-email" />
        <Fact label="Service Area" value={company.serviceArea} testId="home-fact-area" />
      </div>
    </div>
  </section>
);

export const ServicesSection = () => (
  <section id="services" className="section" data-testid="services-section">
    <div className="container">
      <div className="projects-head services-head">
        <SectionTitle title={services.heading} intro={services.intro} />
        <div className="social-note estimate-note" data-testid="free-estimate-note">
          <h3>{services.estimateHeading}</h3>
          <p>{services.estimateText}</p>
          <a href={`tel:${company.phoneLink}`} className="estimate-phone" data-testid="free-estimate-phone">{company.phone}</a>
          <Link to="/contact" className="btn" data-testid="free-estimate-button">Request an Estimate</Link>
        </div>
      </div>
      <ul className="services-list">
        {services.items.map((s, i) => (
          <li key={i} data-testid={`service-item-${i}`}>
            <h3>{s.title}</h3>
            <p>{s.description}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const ProjectsSection = () => {
  const [active, setActive] = useState(null);
  return (
  <section id="projects" className="section" data-testid="projects-section">
    <div className="container">
      <div className="projects-head">
        <SectionTitle title={projects.heading} intro={projects.intro} />
        <div className="social-note" data-testid="projects-social-note">
          <p>{projects.socialNote}</p>
          <SocialLinks place="projects" />
        </div>
      </div>
      {projects.items.map((p, i) => (
        <div key={i} className="project" data-testid={`project-${i}`}>
          <h3>{p.title}</h3>
          {p.description && <p className="project-desc">{p.description}</p>}
          <div className="project-photos">
            {p.photos.map((ph, j) => (
              <figure key={j} data-testid={`project-${i}-photo-${j}`}>
                <button type="button" className="photo-btn" onClick={() => setActive(ph)} aria-label={`Enlarge photo: ${ph.alt}`} data-testid={`project-${i}-photo-${j}-enlarge`}>
                  <img src={ph.src} alt={ph.alt} loading="lazy" />
                  <span className="photo-hint"><Expand size={16} /> View full size</span>
                </button>
                {ph.label && <figcaption>{ph.label}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      ))}
    </div>
    <PhotoViewer photo={active} onClose={() => setActive(null)} />
  </section>
  );
};

export const AboutSection = () => (
  <section id="about" className="section" data-testid="about-section">
    <div className="container about-grid">
      <div>
        <SectionTitle title={about.heading} />
        {about.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <div className="quals">
        <h3 className="sub-heading">{about.qualificationsHeading}</h3>
        <ul className="plain-list" data-testid="qualifications-list">
          {about.qualifications.map((q, i) => <li key={i}><Check size={18} /><span>{q}</span></li>)}
        </ul>
      </div>
    </div>
    {about.photos.length > 0 && (
      <div className="container photo-grid" data-testid="about-photos">
        {about.photos.map((p, i) => <img key={i} src={p.src} alt={p.alt} />)}
      </div>
    )}
  </section>
);

const Row = ({ label, children, testId }) => (
  <div className="contact-row" data-testid={testId}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

export const ContactSection = () => (
  <section id="contact" className="section" data-testid="contact-section">
    <div className="container">
     <div className="contact-grid">
      <div>
        <SectionTitle title={contact.heading} intro={contact.intro} />
        <p className="contact-phone-label">Call or Text</p>
        <a className="contact-phone-big" href={company.phoneLink ? `tel:${company.phoneLink}` : undefined} data-testid="contact-phone-big">{company.phone}</a>
      </div>
      <dl className="contact-list">
        <Row label="Phone" testId="contact-phone">
          {company.phoneLink ? <a href={`tel:${company.phoneLink}`}>{company.phone}</a> : company.phone}
        </Row>
        <Row label="Email" testId="contact-email">
          <a href={`mailto:${company.email}`}>{company.email}</a>
        </Row>
        <Row label="Address" testId="contact-address">{company.address}</Row>
        <Row label="Service Area" testId="contact-service-area">{company.serviceArea}</Row>
        <Row label="Hours" testId="contact-hours">{company.hours}</Row>
      </dl>
     </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="site-footer" data-testid="site-footer">
    <div className="container footer-top">
      <div>
        <img src={home.logo} alt={company.name} className="footer-logo" />
        <div className="footer-contact">
          <div>{company.address}</div>
          <div>{company.phone}</div>
          <a href={`mailto:${company.email}`}>{company.email}</a>
        </div>
      </div>
      <nav className="footer-nav" data-testid="footer-nav">
        {navLinks.map((l) => (
          <Link key={l.id} to={l.path} data-testid={`footer-${l.id}-link`}>{l.label}</Link>
        ))}
      </nav>
      <SocialLinks place="footer" />
    </div>
    <div className="footer-bottom">
      <div className="container">&copy; {new Date().getFullYear()} {company.name}</div>
    </div>
  </footer>
);
